import { useCurrentAccount, useGetDogBreedInterestSubscription } from '@application/api';
import { SeoController } from '@application/components';
import { CACHE_KEY_QUIZ_ANSWERS, CACHE_KEY_QUIZ_EMAIL } from '@application/constants';
import { useNewsletter, useRouter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import { QuizResultsPage } from '@presentation/pages/QuizResults';
import { sortBy } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggleDogBreedInDogBreedInterestSubscription } from '../../SubscribeToDogBreedButtonController/api/toggleDogBreedInDogBreedInterestSubscription';
import { useFormatBreeds } from './api/useFormatBreeds';
import { useGetBreeds } from './api/useGetBreeds';

const HAS_A_DOG_QUESTION_KEY = 5;

export const QuizResultsPageController: FC = () => {
  const { currentAccountLoggedIn, currentAccount } = useCurrentAccount();
  const [newsletterFormSubmitted, setNewsletterFormSubmitted] = useState(false);
  const { toggleDogBreedInDogBreedInterestSubscriptionFromButton } =
    useToggleDogBreedInDogBreedInterestSubscription();

  const { translate, navigateToRoute } = useRouter();

  const answers = useMemo(() => {
    if (typeof window !== 'undefined') {
      return JSON.parse(localStorage.getItem(CACHE_KEY_QUIZ_ANSWERS) ?? '{}');
    }
    return {};
  }, []);

  const hasADog = useMemo(() => {
    if (answers && ['yesCurrently', 'yesPreviously'].includes(answers[HAS_A_DOG_QUESTION_KEY])) {
      return true;
    }
    return false;
  }, [answers]);

  const cachedEmail = useMemo(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CACHE_KEY_QUIZ_EMAIL);
    }
  }, []);

  const { breeds, breedsLoading } = useGetBreeds();

  const restartClickHandler = useCallback(() => {
    localStorage.removeItem(CACHE_KEY_QUIZ_ANSWERS);
    localStorage.removeItem(CACHE_KEY_QUIZ_EMAIL);
    navigateToRoute('quiz');
  }, [navigateToRoute]);

  const { newsletterProps, email, persistHasADog } = useNewsletter({
    shouldShowToast: false,
    shouldTriggerGtm: false,
    onSubmit: (values) => {
      setNewsletterFormSubmitted(true);
      Analytics.sendGenerateLeadEvent('quiz');
      localStorage.setItem(CACHE_KEY_QUIZ_EMAIL, values.email);
    },
    createdFrom: EmailContactCreatedFromEnum.QUIZZ,
    hasADog,
  });

  const subscriptionEmail = currentAccount?.email ?? email ?? cachedEmail;

  const { dogBreedInterestSubscription } = useGetDogBreedInterestSubscription(subscriptionEmail);
  const { formattedBreeds } = useFormatBreeds(answers, breeds, dogBreedInterestSubscription);

  const showNewsletterForm = useMemo(() => {
    if (currentAccountLoggedIn) return false;
    if (cachedEmail) return false;
    if (newsletterFormSubmitted) return false;
    return true;
  }, [cachedEmail, currentAccountLoggedIn, newsletterFormSubmitted]);

  useEffect(() => {
    if (!showNewsletterForm) {
      persistHasADog({ email: subscriptionEmail as string, hasADog });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, hasADog, showNewsletterForm, subscriptionEmail]);

  const sortedBreeds = useMemo(() => {
    return sortBy((x) => x.matchPercentage, formattedBreeds ?? []).reverse();
  }, [formattedBreeds]);

  return (
    <>
      <SeoController
        title={translate('quiz.seoResult.title')}
        description={translate('quiz.seoResult.description')}
      />
      <QuizResultsPage
        answers={answers}
        items={sortedBreeds}
        loading={breedsLoading}
        onRestartClick={restartClickHandler}
        showNewsletterForm={showNewsletterForm}
        newsletterForm={newsletterProps}
        onSubscribeClick={(dogBreedName: string) => {
          if (subscriptionEmail) {
            toggleDogBreedInDogBreedInterestSubscriptionFromButton({
              dogBreedName,
              email: subscriptionEmail,
              hasADog,
            });
          }
        }}
      />
    </>
  );
};
